import {
  call,
  put,
  takeEvery,
  select,
  delay,
  takeLatest,
  race,
  take,
} from "redux-saga/effects";
import { isEqual } from "lodash";
import {
  CHATBOT_INJECT,
  CHATBOT_INJECT_SUCCESS,
  CHATBOT_INIT_SUCCESS,
  CHATBOT_OPEN,
  addAttributes,
  updateAttributes,
  injectChatbotError,
  injectChatbotSuccess,
  initChatbotError,
  initChatbot,
  openChatbotError,
  openChatbotSuccess,
  CHATBOT_LOG_INTAKE,
  logChatIntakeError,
  COMMON_BENEFITS_SUCCESS,
  COMMON_BENEFITS_FAILED,
  NO_COMMON_BENEFITS,
  CHATBOT_UPDATE_ATTRIBUTES,
  updateAttributesError,
  updateAttributesSuccess,
  openChatbot,
  setChatbotFailedFinal,
} from "../action";
import {
  getAmazonConnectSelector,
  getPrimaryLookup,
  getBenefitsOperator,
  getLookupNames,
  getChatbotStatus,
  getSessionId,
  getUsageId,
  getChatAttributes,
  getSubmitRequestStatus,
  getPartnerAppId,
  getChatBotParam,
  getPartnerBrand,
} from "../selector";
import { AMAZON_CONNECT_CHAT_USER_TYPE } from "../app-consts/appConstants";
import { getAttributeNames } from "../screens/CommonLookup/commonLookupHelper";
import {
  getChatbotContactIdFromLocalStorage,
  getSearchQueryFromURL,
  getVaChat,
  promiseInjectAmazonConnect,
  updateChatbotAttributes,
} from "../utils";
import { datadogRum } from "@datadog/browser-rum";
import { getPageNameFromUrl } from "../utils/pageNameHelper";
import { getChatBotFeatureFlag } from "../features";

const VA_CHAT_READY_TIMEOUT_MS = 20000;
const VA_CHAT_READY_POLL_INTERVAL_MS = 250;
const CHATBOT_INIT_TIMEOUT_MS = 15000;
const SYSTEMS_DOWN_CHECK_ATTEMPTS = 2;
const SYSTEMS_DOWN_CHECK_INTERVAL_MS = 500;
const MAX_RETRIES = 6;
const RETRY_DELAY_MS = 1500;
const VA_CHAT_WIDGET_ID = "va_chat_widget";
const VA_CHAT_IFRAME_ID = "va_chat_iframe";
const SYSTEMS_DOWN_TEXT = "systems are down";

export function* injectChatbotSaga() {
  try {
    const amazonConnect = yield select(getAmazonConnectSelector);
    const isInjected = yield call(promiseInjectAmazonConnect, amazonConnect);

    if (isInjected) {
      yield put(injectChatbotSuccess());
    }
  } catch (e) {
    yield put(injectChatbotError());
  }
}

export function* initializeChatSaga() {
  try {
    const amazonConnect = yield select(getAmazonConnectSelector);
    const rescueSubmitted = yield select(getSubmitRequestStatus);
    const usageId = yield select(getUsageId);
    const partnerBrand = (yield select(getPartnerBrand)) as string;

    const defaultAttr = {
      attributes: {
        usageId,
        userType: AMAZON_CONNECT_CHAT_USER_TYPE,
        va_botname: amazonConnect?.botName,
        submission: !rescueSubmitted ? "N" : "Y",
        source: partnerBrand,
      },
    };

    yield put(addAttributes(defaultAttr.attributes));

    getVaChat().set(defaultAttr);
  } catch (e) {
    yield put(initChatbotError());
  }
}

const isVaChatReady = (v: any): boolean =>
  !!v &&
  typeof v.set === "function" &&
  typeof v.initializeChat === "function" &&
  typeof v.open === "function";

export function* openChatSaga(action: ReturnType<typeof openChatbot>) {
  const retryCount = action.payload?.retryCount ?? 0
  try {
    let vaChat = getVaChat();
    if (!isVaChatReady(vaChat)) {
      const deadline = Date.now() + VA_CHAT_READY_TIMEOUT_MS;
      while (!isVaChatReady(vaChat) && Date.now() < deadline) {
        yield delay(VA_CHAT_READY_POLL_INTERVAL_MS);
        vaChat = getVaChat();
      }
      if (!isVaChatReady(vaChat)) {
        throw new Error("Amazon Connect SDK not fully loaded");
      }
    }

    const isConnected = vaChat.__CONFIG__?.isConnected;
    const attr = yield select(getChatAttributes);
    const startPage = getPageNameFromUrl(window.location.pathname)
    const usageId = yield select(getUsageId);
    const mergedAttr = { ...attr, startPage: startPage, usageId : usageId };
    yield put(updateAttributes({...mergedAttr}));
    if (!isConnected) {
      const param = yield select(getChatBotParam);
      vaChat.set({...param, attributes : mergedAttr});
      yield put(initChatbot());
      vaChat.initializeChat();

      const currentStatus = yield select(getChatbotStatus);
      if (currentStatus !== "INITIALIZED") {
        const { timeout } = yield race({
          ready: take(CHATBOT_INIT_SUCCESS),
          timeout: delay(CHATBOT_INIT_TIMEOUT_MS),
        });
        if (timeout) {
          throw new Error("Chatbot init timed out");
        }
      }
    }
    vaChat.open();

    let hasError = false;
    for (let i = 0; i < SYSTEMS_DOWN_CHECK_ATTEMPTS; i++) {
      yield delay(SYSTEMS_DOWN_CHECK_INTERVAL_MS);

      const iframe = document.getElementById(VA_CHAT_WIDGET_ID) as HTMLIFrameElement;
      const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
      if (iframeDoc) {
        const errorText = iframeDoc.body?.textContent || '';
        if (errorText.includes(SYSTEMS_DOWN_TEXT)) {
          hasError = true;
          break;
        }
      }
    }

    if (hasError) {
      const chatIframe = document.getElementById(VA_CHAT_IFRAME_ID);
      if (chatIframe) {
        chatIframe.style.display = 'none';
      }
      throw new Error("Chat failed - systems are down");
    }

    datadogRum.addAction("Start Chat with Attributes", {
      ...mergedAttr,
    });

    yield put(openChatbotSuccess());
  } catch (e) {
    yield put(openChatbotError(e));

    const errorMessage = e instanceof Error ? e.message : String(e);
    const chatbotStatus = yield select(getChatbotStatus);
    const vaChatNow = getVaChat();
    datadogRum.addAction("Chatbot open attempt failed", {
      attempt: retryCount + 1,
      maxAttempts: MAX_RETRIES + 1,
      errorMessage,
      chatbotStatus,
      vaChatPresent: Boolean(vaChatNow),
      vaChatIsConnected: Boolean(vaChatNow?.__CONFIG__?.isConnected),
    });

    if(retryCount < MAX_RETRIES){
      yield delay(RETRY_DELAY_MS);
      yield put(openChatbot({retryCount: (retryCount+1)}))

    } else {
      const conn = (navigator as any).connection;
      const errorContext = {
        errorSource: "openChatSaga",
        message: "Chatbot failed to open after maximum retries",
        errorMessage,
        retryCount,
        maxRetries: MAX_RETRIES,
        chatbotStatus,
        vaChatPresent: Boolean(vaChatNow),
        vaChatIsConnected: Boolean(vaChatNow?.__CONFIG__?.isConnected),
        page: getPageNameFromUrl(window.location.pathname),
        online: navigator.onLine,
        effectiveType: conn?.effectiveType ?? "unknown",
        downlinkMbps: conn?.downlink ?? null,
        rtt: conn?.rtt ?? null,
      };
      datadogRum.addError(
        e instanceof Error ? e : new Error(String(e)),
        errorContext
      );
      yield put(setChatbotFailedFinal());
    }
  }
}

export function* logChatIntakeResultsSaga(action) {
  try {
    // Dependency on experiment group backend work
    const experimentGroup = yield select(getChatBotFeatureFlag);
    const actionName = action.payload;
    // ANI LOOKUP
    const conversationId = getSearchQueryFromURL("conversationId");
    const serviceRequestPayloadUsageId = yield select(getUsageId);
    const sessionId = yield select(getSessionId);
    const pathName = window.location.href;
    let usageId =
      localStorage.getItem("usageId") || serviceRequestPayloadUsageId || "";
    let contactId = getChatbotContactIdFromLocalStorage();

    let data = {
      sessionId,
      pathName,
      conversationId,
      usageId,
      contactId,
      experimentGroup: experimentGroup ? "Experiment" : "Control",
    };

    if (action.payload == "Chat Button Clicked") {
      document.addEventListener(
        "onChatClick",
        function chatOpenEventListener() {
          data.usageId = usageId || localStorage.getItem("usageId");
          data.contactId = contactId || getChatbotContactIdFromLocalStorage();
          datadogRum.addAction(actionName, data);
          document.removeEventListener("onChatClick", chatOpenEventListener);
        }
      );
    } else {
      datadogRum.addAction(actionName, data);
    }
  } catch (e) {
    yield put(logChatIntakeError(e));
  }
}

export function* updateChatAttributesSaga() {
  const chatbotStatus = yield select(getChatbotStatus);
  if (chatbotStatus == "INITIALIZED") {
    const chatBotAttributes = yield select(getChatAttributes);
    const newAttributes = { ...chatBotAttributes };
    const chatbotContactId = getChatbotContactIdFromLocalStorage();
    const newUsageId = yield select(getUsageId);
    const newSessionId = yield select(getSessionId);

    // Adding Lookup name to Attributes
    const isPrimaryLookup = yield select(getPrimaryLookup);
    const operators = yield select(getBenefitsOperator);
    const lookupName = yield select(getLookupNames);
    const startPage = getPageNameFromUrl(window.location.pathname)
    newAttributes.startPage = startPage
    let attrName;
    if (
      lookupName !== undefined &&
      (lookupName.lastName || lookupName.firstName)
    ) {
      attrName = getAttributeNames(isPrimaryLookup, lookupName, operators);
    }

    if (!chatBotAttributes.usageId && newUsageId) {
      newAttributes.usageId = newUsageId;
    }
    if (!chatBotAttributes.sessionId && newSessionId !== "") {
      newAttributes.sessionId = newSessionId;
    }

    if (!isEqual(chatBotAttributes, newAttributes)) {
      if (!chatBotAttributes.contactId && chatbotContactId !== "") {
        newAttributes.contactId = chatbotContactId;
      }

      datadogRum.addAction("Update chat attributes", {
        oldAttrs: chatBotAttributes,
        newAttrs: newAttributes,
      });
      yield put(updateAttributes({ ...newAttributes, ...attrName }));
    }
  }
}

export function* updateChatbotAttributesSaga(action) {
  try {
    updateChatbotAttributes(action.payload);
    yield put(updateAttributesSuccess());
  } catch (e) {
    yield put(updateAttributesError());
  }
}

export function* watchChatbotSaga() {
  yield takeEvery(CHATBOT_INJECT, injectChatbotSaga);
  yield takeEvery(CHATBOT_INJECT_SUCCESS, initializeChatSaga);
  yield takeEvery(CHATBOT_OPEN, openChatSaga);
  yield takeEvery(CHATBOT_LOG_INTAKE, logChatIntakeResultsSaga);
  yield takeLatest(
    [COMMON_BENEFITS_SUCCESS, COMMON_BENEFITS_FAILED, NO_COMMON_BENEFITS],
    updateChatAttributesSaga
  );
  yield takeEvery(CHATBOT_UPDATE_ATTRIBUTES, updateChatbotAttributesSaga);
}
